import { Component, EventEmitter, Injector, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ApiService } from '../../../api/api.service';
import { Model } from '../../../api/model';
import { RouteService } from '../../../routing/services/route.service';
import { EntityService } from '../../../entities/services/entity.service';
import { LoaderService } from '../../../support/loading/loader.service';
import { ToolbarService } from '../../../layouts/toolbar/services/toolbar.service';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { trans } from '../../../localization/helpers';
import 'rxjs/add/operator/take';
import { BUTTONS } from '../../../support';
import { DatatableComponent } from '@kaiser-software/chassis-core/src/app/datatables/components/datatable/datatable.component';
import { UserModel } from '@kaiser-software/chassis-core';
import { MultiTenantService } from '../../../authentication/services/multi-tenant.service';
import { Router } from '@angular/router';
import { HookSystemService } from '@kaiser-software/hook-system';
import { skip, take, takeUntil } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import Timer = NodeJS.Timer;

@Component({
    selector: 'chassis-datatable-layout',
    templateUrl: './datatable-layout.component.html',
    styleUrls: ['./datatable-layout.component.scss']
})
export class DatatableLayoutComponent implements OnInit, OnDestroy {

    /**
     * The page title.
     *
     * @type {string}
     */
    @Input() title: string;

    /**
     * The route prefix.
     *
     * @type {string}
     */
    @Input() routePrefix: string;

    /**
     * The plugin slot prefix.
     *
     * @type {string}
     */
    @Input() pluginPrefix: string;

    @Input() doNotAddId = false;

    /**
     * Toolbar object to override defaults.
     */
    @Input() toolbar: {
        buttons?: Array<string>,
        callbacks?: {
            [key: string]: (resolve: () => void, reject?: () => void) => void
        },
    };

    /**
     * Datatable object to override defaults.
     */
    @Input() table: {
        ajax?: string,
        service?: ApiService,
        callbacks?: {
            [key: string]: Function
        },
        settings?: {
            [key: string]: any
        },
        columns?: Array<any>,
        filters?: Array<any>,
        limit?: number
    };

    @ViewChild(DatatableComponent) public dtComponent: DatatableComponent;

    protected buttonLoader;

    protected datatableInstance: Promise<DataTables.Api>;

    protected user: UserModel;

    protected checkedColumns;

    protected visibleColumns: Array<any> = [];

    protected storeColumnPreferencesTimeout: Timer;

    @Output() init = new EventEmitter();

    protected subscription = new Subscription();

    protected user$: Observable<UserModel>;

    public constructor(
        protected hookSystemService: HookSystemService,
        protected injector: Injector,
        protected authenticationService: AuthenticationService,
        protected entityService: EntityService,
        protected loaderService: LoaderService,
        protected router: Router,
        protected routeService: RouteService,
        protected toolbarService: ToolbarService,
        protected zone: NgZone,
        protected multiTenantService: MultiTenantService,
    ) {
        //
    }

    public ngOnInit(): void {
        this.user$ = this.authenticationService.user$
            .pipe(
                take(1),
            );

        this.authenticationService.user$
            .pipe(
                skip(1),
                take(1),
            )
            .subscribe(() => {
                this.subscription.unsubscribe();
            });

        this.subscription.add(
            this.user$
                .subscribe((user) => {
                    this.user = user;
                })
        );

        this.createDatatable();

        // When the user is changed we go back to the home page.
        // This way we prevent permission and data table errors.
        this.authenticationService.user$
            .pipe(
                skip(1),
                take(1),
            )
            .subscribe(() => {
                this.router.navigate(['/'], {
                    replaceUrl: true,
                    queryParams: null,
                });
            });

        // When the tenant is changed we reload the data table,
        // unless the impersonation is changed. Otherwise the data
        // table will reload even when it is not wanted.
        this.subscription.add(
            this.multiTenantService.getScope()
                .pipe(
                    skip(1),
                    takeUntil(
                        this.authenticationService.stoppingImpersonation$,
                    ),
                    takeUntil(
                        this.authenticationService.startingImpersonation$,
                    ),
                )
                .subscribe(() => {
                    this.datatableInstance
                        .then((table) => {
                            table.ajax.reload();
                        });
                })
        );

        this.createToolbar();
    }

    protected createDatatable(): void {
        if (!this.table) {
            this.table = {};
        }

        if (!this.table.settings) {
            this.table.settings = {};
        }

        // when ajax action is not set
        // set the default
        if (!this.table.ajax) {
            this.table.ajax = this.table.service.all(true, {
                datatable: true
            });
        }

        this.createDatatableCallbacks();
        this.createDatatableColumns();
        this.createDatatableFilters();
        this.restoreColumnPreferences();
        this.saveVisibleColumns();
    }

    /**
     * Create the datatable columns.
     */
    protected createDatatableColumns() {
        // Check if columns are given
        if (!this.table.columns) {
            this.table.columns = [];
        }

        if (this.table.columns.length === 0) {
            this.table.columns.push('*');
        }

        if (this.table.limit === undefined) {
            this.table.limit = 20;
        }

        this.table.columns = this.hookSystemService.filter(this.pluginPrefix + '-columns', this.table.columns);

        // Get the wildcard position
        const wildcardIndex = this.table.columns.indexOf('*');

        if (wildcardIndex >= 0) {
            // Get all the columns before the wildcard position
            const before = this.table.columns.filter((value, index) => {
                return index < wildcardIndex;
            });

            // Get all the columns after the wildcard position
            const after = this.table.columns.filter((value, index) => {
                return index > wildcardIndex;
            });

            // Empty the table columns
            this.table.columns = [];

            // Add all columns before the wildcard position
            this.table.columns = this.table.columns.concat(before);

            // When the wildcard index is set we'll display the
            // id column as default
            this.table.columns = [{
                name: 'id',
                data: 'id',
                title: 'Id',
                class: 'id-column',
                visible: true,
                searchable: true,
                filter: {
                    filter_type: 'text',
                    filter_delay: 1000,
                }
            }].concat(this.table.columns);

            // We'll replace the wildcard position position with all existing columns
            if (this.table.service && this.table.service.entityName) {
                this.table.columns = this.table.columns.concat(
                    this.entityService.getDatatableColumns(this.table.service.entityName)
                        .filter((column) => {
                            return !this.table.columns.find((c) => {
                                return c.name === column.name;
                            });
                        })
                );
            }

            // Add all columns after the wildcard position
            this.table.columns = this.table.columns.concat(after);

            this.table.columns = this.hookSystemService.filter(this.pluginPrefix + '-columns-after', this.table.columns);
        }

        this.setColumnOrdering();
    }

    /**
     * Create the database callbacks.
     */
    protected createDatatableCallbacks() {
        if (!this.table.callbacks) {
            this.table.callbacks = {};
        }

        if (!this.table.callbacks['click']) {
            this.createClickCallback();
        }
    }

    /**
     * Create the default click callback.
     */
    protected createClickCallback() {
        this.table.callbacks['click'] = (model: Model, row: any, index: number, event: any) => {
            // Fix for not resolving route completely when filtering and clicking
            this.zone.run(() => {
                // If the clicked column has a click event
                if (!isNaN(event.currentTarget.cellIndex) && this.visibleColumns[event.currentTarget.cellIndex].hasOwnProperty('click') && typeof this.visibleColumns[event.currentTarget.cellIndex].click === 'function') {
                    this.visibleColumns[event.currentTarget.cellIndex].click(model, row, index, event);
                } else if (this.table.callbacks && this.table.callbacks.edit) {
                    this.table.callbacks.edit(model, row, index);
                } else {
                    this.routeService.navigate(this.routePrefix + '.edit', model.id);
                }
            });
        };
    }

    /**
     * Create column filters.
     */
    private createDatatableFilters() {
        if (!this.table.filters) {
            this.table.filters = [];
        }
    }

    /**
     * Retrieve the datatable instance
     *
     * @param datatableComponent
     */
    protected onInstance(datatableComponent) {
        this.datatableInstance = datatableComponent.datatableElement.dtInstance;

        this.init.emit(this.datatableInstance);

        this.datatableInstance.then(table => {

            table.on('column-reorder', () => {
                this.persistDatatablePreferences(table);
            });
        });
    }

    /**
     * Prevent the dropdown menu from closing on click
     *
     * @param event
     */
    protected clickColumnToggle(event) {
        event.stopPropagation();

        this.saveVisibleColumns();
        this.saveColumnPreferences();
    }

    /**
     * Toggle the column clicked
     *
     * @param {number} i
     * @param visible
     */
    protected toggleColumn(i: number, visible) {
        if (this.datatableInstance !== undefined) {
            this.datatableInstance.then(instance => {
                instance.column(i).visible(visible);

                // if (this.toggleColumnsTimeout) {
                //     clearTimeout(this.toggleColumnsTimeout);
                // }

                // this.toggleColumnsTimeout = setTimeout(() => {
                // instance.draw();
                // }, 850);
            });
        }
    }

    /**
     * Create the toolbar.
     */
    protected createToolbar() {
        this.buttonLoader = this.loaderService.create('addLoader');

        this.subscription.add(
            this.user$
                .subscribe((user) => {
                    // Check if the user has permissions to write
                    if (this.userCanCreate(user)) {
                        if (!this.toolbar || this.toolbar && this.toolbar.buttons && this.toolbar.buttons.indexOf('add') >= 0) {
                            this.addAddButton();
                        }
                    }
                })
        );
    }

    protected userCanCreate(user) {
        if (this.table.service.entityName) {
            return user.can(this.table.service.entityName + '.write') || user.can('entities.' + this.table.service.entityName + '.write');
        }

        if (this.table.service.permissionName) {
            return user.can(this.table.service.permissionName + '.write') || user.can('entities.' + this.table.service.permissionName + '.write');
        }

        return false;
    }

    /**
     * Add an add button to the toolbar.
     */
    private addAddButton() {
        this.toolbarService.add({
            name: BUTTONS.ADD.NAME,
            text: trans('Add'),
            icon: BUTTONS.ADD.ICON,
            classes: 'btn-primary',
            callback: () => {
                return this.table.callbacks && this.table.callbacks.create ?
                    this.table.callbacks.create() : this.addCallback();
            }
        });
    }

    /**
     * The callback function that will be executed when the add button is pressed.
     */
    private addCallback() {
        this.buttonLoader.start();

        const resolve = () => {
            this.buttonLoader.stop();
        };

        const reject = () => {
            this.buttonLoader.stop();
        };

        if (this.toolbar && this.toolbar.callbacks && this.toolbar.callbacks['submit']) {
            this.toolbar.callbacks['submit'](resolve, reject);

            return;
        }

        this.routeService.navigate(this.routePrefix + '.create');

        resolve();
    }

    protected destroyToolbar() {
        this.loaderService.destroy('addLoader');
        this.toolbarService.clear();
    }

    /**
     * Save preferences to local storage
     *
     */
    private saveColumnPreferences() {
        this.checkedColumns = this.table.columns.filter(column => column.visible).map(column => column.name);

        this.datatableInstance.then(table => {
            this.persistDatatablePreferences(table);
        });
    }

    /**
     * Restore column visibility preference
     *
     */
    private restoreColumnPreferences() {
        this.authenticationService.user$
            .subscribe(user => {
                const columnPreferences = user.datatable_preferences ? user.datatable_preferences['visibleColumns'] : false;

                if (columnPreferences) {
                    const visibleColumns = columnPreferences[this.getTableName()] || this.table.columns.filter(column => column.visible).map(column => column.name);

                    this.table.columns.map(column => {

                        if (visibleColumns.indexOf(column.name) !== -1) {
                            column.visible = true;
                        } else {
                            column.visible = false;
                        }

                        return column;

                    });
                }

            });
    }

    /**
     * Set the default column ordering based on default and preferences
     *
     */
    private setColumnOrdering() {

        this.init.subscribe(() => {

            this.datatableInstance.then((table: any) => {

                const orderPref = this.user.datatable_preferences ? this.user.datatable_preferences['orderingPreferences'] : false;

                if (orderPref) {
                    let orderPrefParsed = orderPref[this.getTableName()] || {};

                    // check if column length is the same
                    if (orderPrefParsed.length !== this.table.columns.length) {
                        // not the same -> resync with saved defaults
                        orderPrefParsed = this.syncColumnOrderPreferences(orderPrefParsed);
                    }

                    if (table.colReorder) {
                        table.colReorder.order(orderPrefParsed);
                    }

                } else {
                    if (table.colReorder) {
                        table.colReorder.order(this.table.columns.map((column, index) => index));
                    }
                }

            });

        });
    }

    /**
     * Resync column indexes if preferences column length differ with given column length
     *
     * @param columnOrder
     */
    private syncColumnOrderPreferences(columnOrder: Array<number>) {

        if (columnOrder.length > this.table.columns.length) {

            return columnOrder.filter(orderValue => orderValue < this.table.columns.length);

        } else if (columnOrder.length < this.table.columns.length) {

            const columnsShort = this.table.columns.length - columnOrder.length;
            const lastColumn   = this.table.columns.length - columnsShort;

            for (let i = 0; i < columnsShort; i++) {
                columnOrder = columnOrder.concat([lastColumn + i]);
            }

            return columnOrder;

        }

    }

    private persistDatatablePreferences(table) {
        if (this.storeColumnPreferencesTimeout) {
            clearTimeout(this.storeColumnPreferencesTimeout);
        }

        this.storeColumnPreferencesTimeout = setTimeout(() => {
            const orderingPreferences = this.user.datatable_preferences ? this.user.datatable_preferences['orderingPreferences'] : {};

            const newOrderingPreferences                = Object.assign({}, orderingPreferences);
            newOrderingPreferences[this.getTableName()] = table['colReorder'].order();

            const newVisibleColumns                = {};
            newVisibleColumns[this.getTableName()] = this.checkedColumns;

            if (orderingPreferences !== newOrderingPreferences) {

                // merge preferences with datatablePreferences object in user
                const datatablePreferences = Object.assign(this.user.datatable_preferences || {}, {
                    orderingPreferences: newOrderingPreferences,
                    visibleColumns: newVisibleColumns
                });

                // save to Rest API
                this.authenticationService
                    .updateUser({
                        datatable_preferences: datatablePreferences
                    })
                    .subscribe();

            } else {
                console.warn('No change in preferences -> do not persist');
            }
        }, 1000);
    }

    /**
     * Get the generated table name based on url
     *
     */
    private getTableName() {
        return this.router.url.split('?')[0];
    }

    /**
     * Save the columns that are currently visible
     */
    protected saveVisibleColumns() {
        this.visibleColumns = this.table.columns.filter(column => column.visible);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();

        this.destroyToolbar();
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/datatables/components/datatable-layout/datatable-layout.component.ts